import { useState, useEffect, useMemo } from 'react';
import { Button, Input, Modal, Table, Card, useToast } from '../components/common';
import api from '../services/api';
import type { User } from '../types';

interface UserFormData {
  email: string;
  username: string;
  password: string;
  is_superuser: boolean;
  is_active: boolean;
}

type RoleFilter = 'all' | 'admin' | 'user';
type StatusFilter = 'all' | 'active' | 'inactive';

export default function UsersPage() {
  const { addToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '', username: '', password: '', is_superuser: false, is_active: true
  });
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get<User[]>('/api/v1/users');
      setUsers(response.data);
    } catch (error) {
      addToast('error', '無法載入用戶列表');
    } finally {
      setLoading(false);
    }
  };

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!user.email.toLowerCase().includes(query) && 
            !user.username.toLowerCase().includes(query)) {
          return false;
        }
      }
      // Role filter
      if (roleFilter === 'admin' && !user.is_superuser) return false;
      if (roleFilter === 'user' && user.is_superuser) return false;
      // Status filter
      if (statusFilter === 'active' && !user.is_active) return false;
      if (statusFilter === 'inactive' && user.is_active) return false;
      return true;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData: Partial<UserFormData> = { username: formData.username, is_superuser: formData.is_superuser, is_active: formData.is_active };
        if (formData.password) updateData.password = formData.password;
        await api.put(`/api/v1/users/${editingUser.id}`, updateData);
        addToast('success', '用戶已更新');
      } else {
        await api.post('/api/v1/users', formData);
        addToast('success', '用戶已建立');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '操作失敗');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/api/v1/users/${deleteConfirm.id}`);
      addToast('success', '用戶已刪除');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '刪除失敗');
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({ email: user.email, username: user.username, password: '', is_superuser: user.is_superuser, is_active: user.is_active });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ email: '', username: '', password: '', is_superuser: false, is_active: true });
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatTokens = (tokens: number) => {
    if (tokens < 1000) return tokens.toString();
    if (tokens < 1000000) return `${(tokens / 1000).toFixed(1)}K`;
    return `${(tokens / 1000000).toFixed(2)}M`;
  };

  const columns = [
    { key: 'email', header: '電子郵件' },
    { key: 'username', header: '用戶名稱' },
    { key: 'api_key_count', header: 'API Key 數量' },
    { key: 'total_requests', header: '總請求數' },
    { key: 'total_traffic_bytes', header: '總流量', render: (u: User) => formatBytes(u.total_traffic_bytes || 0) },
    { key: 'total_tokens', header: '總 Token', render: (u: User) => formatTokens(u.total_tokens || 0) },
    { key: 'is_superuser', header: '角色', render: (u: User) => (
      <span className={`px-2 py-1 rounded text-xs ${u.is_superuser ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
        {u.is_superuser ? '管理員' : '用戶'}
      </span>
    )},
    { key: 'is_active', header: '狀態', render: (u: User) => (
      <span className={`px-2 py-1 rounded text-xs ${u.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
        {u.is_active ? '啟用' : '停用'}
      </span>
    )},
    { key: 'created_at', header: '建立時間', render: (u: User) => new Date(u.created_at).toLocaleDateString('zh-TW') },
    { key: 'actions', header: '操作', render: (u: User) => (
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" onClick={() => openEditModal(u)}>編輯</Button>
        <Button size="sm" variant="danger" onClick={() => setDeleteConfirm(u)}>刪除</Button>
      </div>
    )},
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">用戶管理</h1>
        <Button onClick={openCreateModal}>新增用戶</Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">搜尋</label>
            <input
              type="text"
              placeholder="搜尋電子郵件或用戶名稱..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="w-32">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">角色</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">全部</option>
              <option value="admin">管理員</option>
              <option value="user">用戶</option>
            </select>
          </div>
          <div className="w-32">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">狀態</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">全部</option>
              <option value="active">啟用</option>
              <option value="inactive">停用</option>
            </select>
          </div>
          {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
            <Button 
              variant="secondary" 
              size="sm"
              onClick={() => { setSearchQuery(''); setRoleFilter('all'); setStatusFilter('all'); }}
            >
              清除篩選
            </Button>
          )}
        </div>
        {filteredUsers.length !== users.length && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            顯示 {filteredUsers.length} / {users.length} 位用戶
          </p>
        )}
      </Card>

      <Card>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <Table data={filteredUsers} columns={columns} keyExtractor={(u) => u.id} emptyMessage="暫無用戶" />
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingUser ? '編輯用戶' : '新增用戶'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="電子郵件" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required disabled={!!editingUser} />
          <Input label="用戶名稱" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required />
          <Input label={editingUser ? '新密碼（留空則不變更）' : '密碼'} type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingUser} />
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.is_superuser} onChange={(e) => setFormData({ ...formData, is_superuser: e.target.checked })} className="rounded" />
              <span className="text-sm text-gray-700 dark:text-gray-300">管理員權限</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
              <span className="text-sm text-gray-700 dark:text-gray-300">啟用帳號</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>取消</Button>
            <Button type="submit">{editingUser ? '更新' : '建立'}</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="確認刪除">
        <p className="text-gray-600 dark:text-gray-400 mb-4">確定要刪除用戶 <strong>{deleteConfirm?.email}</strong> 嗎？此操作無法復原。</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>取消</Button>
          <Button variant="danger" onClick={handleDelete}>確認刪除</Button>
        </div>
      </Modal>
    </div>
  );
}
